import { useRouter } from "next/router"
import { BaseRouter } from "next/dist/shared/lib/router/router"

type RecordParams<T> = Record<string, T | T[]>
type Params = { id?: string | number }
type CustomOption<T> = { default?: T }
type GetParamsOptions = { toArray?: boolean }

export function useQueryParams() {
  const { query, pathname, push } = useRouter()

  const updateQuery = (queryString: Pick<BaseRouter, "pathname" | "query">) =>
    push(queryString, undefined, { shallow: true })

  const addParams = (key: string, value: string | string[]) => {
    query[key] = value
    updateQuery({ pathname, query: { ...query } })
  }

  const deleteParams = (key: string) => {
    const currentQuery = { ...query }
    delete currentQuery[key]

    updateQuery({ pathname, query: currentQuery })
  }

  const getParams = <T extends string | string[]>(
    key: string,
    options?: GetParamsOptions
  ): T => {
    const findQuery = query[key] || ""

    if (options?.toArray || Array.isArray(findQuery)) {
      return (Array.isArray(findQuery) ? findQuery : [findQuery]) as T
    }

    return findQuery as T
  }

  const putUpdate = <T extends Pick<Params, "id">>(
    filterParams: RecordParams<T>
  ) => {
    const originQuery = { ...query }

    const newQueryByParams = Object.entries(filterParams).reduce(
      (acc: Record<string, any>, [key, ids]) => {
        if (!Array.isArray(ids)) ids = [ids]
        const values = ids.map(({ id }) => id) // Object에서 id만 추출

        acc[key] = values
        return acc
      },
      {}
    )

    updateQuery({
      pathname: pathname,
      query: { ...originQuery, ...newQueryByParams },
    })
  }

  const getUrlReturnOptions = <T extends string>(
    key: string,
    findLabel: (value: T) => string,
    customOption?: CustomOption<T>
  ): { id: string; label: string }[] => {
    const values = getParams<T>(key)
    if (!values) {
      // url에 key로 찾은 value가 없는 경우
      if (!customOption?.default) return [] // 초기값 설정이 없을 경우

      return [
        { id: customOption.default, label: findLabel(customOption.default) },
      ]
    }

    if (!Array.isArray(values))
      // 단일 요소일 경우 Array로 통일하여 return
      return [{ id: values, label: findLabel(values) }]

    return values.map((id) => ({ id, label: findLabel(id) }))
  }

  return { addParams, deleteParams, getParams, putUpdate, getUrlReturnOptions }
}
